var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/* eslint-disable no-continue */
import React, { useMemo } from 'react';
import isEqual from 'lodash.isequal';
import { processMessages } from './utils';
import { InfiniteScroll } from '../InfiniteScrollPaginator';
import { Message } from '../Message';
import { isDate } from '../../context/TranslationContext';
// fast since it usually iterates just the last few messages
var getLastReceived = function (messages) {
    for (var i = messages.length - 1; i > 0; i -= 1) {
        if (messages[i].status === 'received') {
            return messages[i].id;
        }
    }
    return null;
};
var getReadStates = function (messages, read) {
    if (read === void 0) { read = {}; }
    // create object with empty array for each message id
    var readData = {};
    Object.values(read).forEach(function (readState) {
        if (!readState.last_read)
            return;
        var userLastReadMsgId;
        messages.forEach(function (msg) {
            //@ts-expect-error
            if (msg.updated_at < readState.last_read)
                userLastReadMsgId = msg.id;
        });
        if (userLastReadMsgId) {
            if (!readData[userLastReadMsgId])
                readData[userLastReadMsgId] = [];
            readData[userLastReadMsgId].push(readState.user);
        }
    });
    return readData;
};
var insertIntro = function (messages, headerPosition) {
    var newMessages = messages;
    var intro = { type: 'channel.intro' };
    // if no headerPosition is set, HeaderComponent will go at the top
    if (!headerPosition) {
        newMessages.unshift(intro);
        return newMessages;
    }
    // if no messages, intro gets inserted
    if (!newMessages.length) {
        newMessages.unshift(intro);
        return newMessages;
    }
    // else loop over the messages
    for (var i = 0, l = messages.length; i < l; i += 1) {
        var message = messages[i];
        var messageTime = message.created_at
            ? //@ts-expect-error
                message.created_at.getTime()
            : null;
        var nextMessageTime = messages[i + 1] && messages[i + 1].created_at
            ? //@ts-expect-error
                messages[i + 1].created_at.getTime()
            : null;
        // header position is smaller than message time so comes after;
        if (messageTime < headerPosition) {
            // if header position is also smaller than message time continue;
            if (nextMessageTime < headerPosition) {
                if (messages[i + 1] && messages[i + 1].type === 'message.date')
                    continue;
                if (!nextMessageTime) {
                    newMessages.push(intro);
                    return newMessages;
                }
                continue;
            }
            else {
                newMessages.splice(i + 1, 0, intro);
                return newMessages;
            }
        }
    }
    return newMessages;
};
var getGroupStyles = function (message, previousMessage, nextMessage, noGroupByUser) {
    var _a, _b, _c, _d, _e, _f, _g;
    if (message.type === 'message.date')
        return '';
    if (message.type === 'channel.event')
        return '';
    if (message.type === 'channel.intro')
        return '';
    if (noGroupByUser || ((_a = message.attachments) === null || _a === void 0 ? void 0 : _a.length) !== 0)
        return 'single';
    var isTopMessage = !previousMessage ||
        previousMessage.type === 'channel.intro' ||
        previousMessage.type === 'message.date' ||
        previousMessage.type === 'system' ||
        previousMessage.type === 'channel.event' ||
        ((_b = previousMessage.attachments) === null || _b === void 0 ? void 0 : _b.length) !== 0 ||
        ((_c = message.user) === null || _c === void 0 ? void 0 : _c.id) !== ((_d = previousMessage.user) === null || _d === void 0 ? void 0 : _d.id) ||
        previousMessage.type === 'error' ||
        previousMessage.deleted_at;
    var isBottomMessage = !nextMessage ||
        nextMessage.type === 'message.date' ||
        nextMessage.type === 'system' ||
        nextMessage.type === 'channel.event' ||
        nextMessage.type === 'channel.intro' ||
        ((_e = nextMessage.attachments) === null || _e === void 0 ? void 0 : _e.length) !== 0 ||
        ((_f = message.user) === null || _f === void 0 ? void 0 : _f.id) !== ((_g = nextMessage.user) === null || _g === void 0 ? void 0 : _g.id) ||
        nextMessage.type === 'error' ||
        nextMessage.deleted_at;
    if (!isTopMessage && !isBottomMessage) {
        if (message.deleted_at || message.type === 'error')
            return 'single';
        return 'middle';
    }
    if (isBottomMessage) {
        if (isTopMessage || message.deleted_at || message.type === 'error')
            return 'single';
        return 'bottom';
    }
    if (isTopMessage)
        return 'top';
    return '';
};
var UnMemoizedMessageListInner = function (props) {
    var bottomRef = props.bottomRef, channel = props.channel, client = props.client, DateSeparator = props.DateSeparator, _a = props.disableDateSeparator, disableDateSeparator = _a === void 0 ? false : _a, EmptyStateIndicator = props.EmptyStateIndicator, HeaderComponent = props.HeaderComponent, headerPosition = props.headerPosition, _b = props.hideDeletedMessages, hideDeletedMessages = _b === void 0 ? false : _b, _c = props.hideNewMessageSeparator, hideNewMessageSeparator = _c === void 0 ? false : _c, internalInfiniteScrollProps = props.internalInfiniteScrollProps, internalMessageProps = props.internalMessageProps, messages = props.messages, MessageSystem = props.MessageSystem, noGroupByUser = props.noGroupByUser, onMessageLoadCaptured = props.onMessageLoadCaptured, read = props.read, threadList = props.threadList, TypingIndicator = props.TypingIndicator;
    var lastRead = useMemo(function () { var _a; return (_a = channel.lastRead) === null || _a === void 0 ? void 0 : _a.call(channel); }, [channel]);
    var enrichMessages = function () {
        var messageWithDates = threadList || (disableDateSeparator && !hideDeletedMessages && hideNewMessageSeparator)
            ? messages
            : processMessages(messages, lastRead, client.userID, hideDeletedMessages, disableDateSeparator, hideNewMessageSeparator);
        if (HeaderComponent) {
            return insertIntro(messageWithDates, headerPosition);
        }
        return messageWithDates;
    };
    var enrichedMessages = enrichMessages();
    var messageGroupStyles = useMemo(function () {
        return enrichedMessages.reduce(function (acc, message, i) {
            var style = getGroupStyles(message, enrichedMessages[i - 1], enrichedMessages[i + 1], noGroupByUser);
            if (style)
                acc[message.id] = style;
            return acc;
        }, {});
    }, [enrichedMessages, noGroupByUser]);
    // get the readData, but only for messages submitted by the user themselves
    var userID = client.userID;
    var readData = useMemo(function () {
        return getReadStates(enrichedMessages.filter(function (_a) {
            var user = _a.user;
            return (user === null || user === void 0 ? void 0 : user.id) === userID;
        }), read);
    }, [userID, enrichedMessages, read]);
    var lastReceivedId = useMemo(function () { return getLastReceived(enrichedMessages); }, [enrichedMessages]);
    var elements = useMemo(function () {
        return enrichedMessages.map(function (message) {
            var _a;
            if (message.type === 'message.date' && message.date && isDate(message.date)) {
                return (React.createElement("li", { key: message.date.toISOString() + "-i" },
                    React.createElement(DateSeparator, { date: message.date, unread: message.unread })));
            }
            if (message.type === 'channel.intro' && HeaderComponent) {
                return (React.createElement("li", { key: 'intro' },
                    React.createElement(HeaderComponent, null)));
            }
            if (message.type === 'channel.event' || message.type === 'system') {
                if (!MessageSystem)
                    return null;
                return (React.createElement("li", { key: ((_a = message.event) === null || _a === void 0 ? void 0 : _a.created_at) ||
                        message.created_at ||
                        '' },
                    React.createElement(MessageSystem, { message: message })));
            }
            if (message.type !== 'message.read') {
                var groupStyles = messageGroupStyles[message.id] || '';
                return (React.createElement("li", { className: "str-chat__li str-chat__li--" + groupStyles, key: message.id || message.created_at, onLoadCapture: onMessageLoadCaptured },
                    React.createElement(Message, __assign({ client: client, groupStyles: [groupStyles], lastReceivedId: lastReceivedId, message: message, readBy: readData[message.id] || [], threadList: threadList }, internalMessageProps))));
            }
            return null;
        });
    }, [
        client,
        enrichedMessages,
        internalMessageProps,
        lastReceivedId,
        messageGroupStyles,
        MessageSystem,
        onMessageLoadCaptured,
        readData,
        threadList,
    ]);
    if (!elements.length && EmptyStateIndicator) {
        return React.createElement(EmptyStateIndicator, { listType: 'message' });
    }
    return (React.createElement(InfiniteScroll, __assign({ className: 'str-chat__reverse-infinite-scroll', "data-testid": 'reverse-infinite-scroll', isReverse: true, useWindow: false }, internalInfiniteScrollProps),
        React.createElement("ul", { className: 'str-chat__ul' }, elements),
        React.createElement(TypingIndicator, { threadList: threadList }),
        React.createElement("div", { key: 'bottom', ref: bottomRef })));
};
export var MessageListInner = React.memo(UnMemoizedMessageListInner, isEqual);
